import { motion } from "framer-motion";

export default function WhyDeepDive() {
  return (
    <section data-testid="why-deepdive" className="py-20 md:py-28 bg-background">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-3">The deeper case for drone spraying</div>
        <h2 className="font-display text-3xl sm:text-4xl text-primary font-bold leading-tight">
          Why South Florida agriculture is switching to drone application
        </h2>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-8 space-y-6 text-foreground/85 leading-relaxed text-base md:text-lg"
        >
          <p>
            Drone crop spraying isn't a gimmick — it's the most significant change in agricultural application
            technology since the introduction of the manned crop duster in the 1920s. For South Florida growers
            working avocado groves in the Redland, tomato fields along Krome Avenue, ornamental nurseries in
            Homestead, sugarcane on the Glades edge, and specialty crops across Broward and Palm Beach County,
            the economics and agronomy now favor drones across the vast majority of fields.
          </p>

          <h3 className="font-display text-xl md:text-2xl font-bold text-primary pt-3">
            FAA Part 137 regulation makes commercial drone spraying legal — and accountable
          </h3>
          <p>
            Every commercial agricultural drone application in Florida is governed by three overlapping regulatory
            frameworks. The pilot must hold an FAA Part 107 remote-pilot license. The operating company must hold an
            FAA Part 137 agricultural operator certificate — the same Part 137 that governs manned crop dusters.
            And the applicator must hold a Florida Department of Agriculture & Consumer Services (FDACS) commercial
            applicator license. Every AeroAcres flight is logged, geofenced and tied back to those three credentials,
            which is what makes our coverage reports valid for organic certification audits and crop-insurance claims.
          </p>

          <h3 className="font-display text-xl md:text-2xl font-bold text-primary pt-3">
            The cost case: 20–30% lower chemical, 30× faster than ground rigs
          </h3>
          <p>
            Per-acre costs for drone spraying in Miami-Dade typically run $15–$25 for pesticide, $18–$26 for
            fungicide and $13–$20 for foliar fertilizer. That's competitive with — and often cheaper than — running
            a tractor sprayer when you factor in fuel, labor, equipment depreciation and crop damage from tire
            tracks. More importantly, drones cut chemical use by 20–30% through GPS-guided swath control, tighter
            overlap management and electrostatic nozzle technology. On a 60-acre avocado grove sprayed five times a
            season, that's thousands of dollars in chemical savings every year — savings that go straight to the
            grower's bottom line.
          </p>

          <h3 className="font-display text-xl md:text-2xl font-bold text-primary pt-3">
            Environmental benefits that matter for the next decade of South Florida farming
          </h3>
          <p>
            Drone application is the most environmentally responsible spray method available today. Zero soil
            compaction protects the structure of South Florida's already-thin limestone soils. Lower chemical
            volumes mean less runoff into the Biscayne Aquifer and the canals that drain into Biscayne Bay.
            Precision GPS swath control eliminates the off-target drift that plagues manned aerial applicators
            in pollinator-sensitive borders. And because drones electric-power their props, every flight is
            quieter than a tractor and produces zero ground-level diesel emissions — a real win for the workers
            and the surrounding residential pockets near Homestead, Princeton and Naranja.
          </p>

          <h3 className="font-display text-xl md:text-2xl font-bold text-primary pt-3">
            Why time-to-mobilization decides outcomes
          </h3>
          <p>
            South Florida's pest and disease cycles move fast. An anthracnose outbreak in a mango grove or a
            whitefly bloom in a tomato field can do six figures of damage in 72 hours. A ground crew with a tractor
            sprayer needs days to schedule, especially during planting or harvest. A manned crop duster needs an
            airport and a minimum commercial-scale field. A drone team can mobilize within 24–48 hours from our
            Homestead base, often the same day for emergencies. That speed advantage — combined with the agronomic
            and economic case — is why a growing share of Miami-Dade, Broward and Palm Beach County agriculture
            is now sprayed by drone, and why we expect that share to keep climbing through the rest of the decade.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
